<template>
  <app-layout>
    <template #header>
      <div class="flex flex-row justify-between items-center">
        <h2 class="font-bold text-xl text-gray-800 leading-tight">
          <em class="fas fa-credit-card" /> Cartão
        </h2>
        <balance :short="true" />
      </div>
    </template>
    <div class="py-12 pt-10 pb-6">
      <flash :timeout="5000" />
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pt-10">
        <div class="flex items-center justify-between mb-6">
          <h2
            class="font-bold text-xl text-gray-800 leading-tight cursor-pointer"
            @click="redirectIndex"
          >
            <em class="fas fa-arrow-left" /> Solicitações de cartão
          </h2>
          <button-confirmation
            @click.native="requestCard = true"
            :disabled="requestValidation"
          >
            Solicitar novo cartão
          </button-confirmation>
        </div>
        <div class="bg-white shadow-xl sm:rounded-lg px-6 py-4">
          <div>
            <inertia-table
              :tableClasses="'text-xs font-normal text-black text-opacity-60'"
              :enable-search="false"
              :data="{ list: cards.list.prepaid_cards }"
              :params="params"
              :columns="columns"
              :enableExport="false"
              :hasIcon="false"
            >
              <template slot-scope="{ item }" slot="status">
                <div class="py-2">
                  <span
                    :class="getStatus(item.request_status).class"
                    class="rounded-full px-2 py-1 whitespace-nowrap font-normal"
                  >
                    {{ getStatus(item.request_status).name }}
                    <i :class="getStatus(item.request_status).icon" />
                  </span>
                </div>
              </template>
              <template slot-scope="{ item }" slot="request">
                <div class="flex flex-col whitespace-nowrap">
                  <span class="text-black text-opacity-60 text-base">
                    Cartão de crédito pré-pago
                  </span>
                  <span class="text-black text-opacity-60 text-xs">
                    <em class="fab fa-cc-mastercard"></em>
                    **** **** ****{{
                      ["active", "theft_or_loss", "canceled"].includes(item.status)
                        ? item.last_4_digits
                        : "****"
                    }}
                  </span>
                </div>
              </template>
              <template slot-scope="{ item }" slot="requester">
                <div>
                  <p class="text-black text-opacity-60 text-sm">
                    {{ item.embossing_name | capitalizeName }}
                  </p>
                  <p class="text-black text-opacity-60 text-xs">
                    <i class="fas fa-id-card" />
                    {{ item.document | federalDocument }}
                  </p>
                </div>
              </template>
              <template slot-scope="{ item }" slot="request_date">
                <div class="text-black text-opacity-60">
                  {{ item.created | smallDateTime }}
                </div>
              </template>
              <template slot-scope="{ item }" slot="delivery_forecast">
                <div
                  class="text-black text-opacity-60"
                  v-if="item.address && item.address.state"
                >
                  {{ calcDeliveryForecast(item.created, item.address.state) }}
                </div>
                <div v-else>-----</div>
              </template>

              <template slot-scope="{ item }" slot="action">
                <div class="text-gray-500 flex gap-4">
                  <button
                    @click.stop="
                      item.request_status === 'approved'
                        ? showReportModal(item)
                        : null
                    "
                    class="w-8 h-8 rounded-full border bg-white shadow-lg"
                    :class="
                      item.request_status !== 'approved'
                        ? 'bg-gray-500 border-gray-500 opacity-30 cursor-not-allowed'
                        : ''
                    "
                    content="Reportar problema"
                    v-tippy
                  >
                    <em
                      class="text-black text-xs fas fa-exclamation-circle"
                    ></em>
                  </button>

                  <button
                    @click.stop="
                      item.request_status === 'approved'
                        ? showPasswordModal(item)
                        : null
                    "
                    class="w-8 h-8 rounded-full bg-yellow-500 shadow-lg"
                    :class="
                      item.request_status !== 'approved'
                        ? 'bg-gray-500 border-gray-500 opacity-30 cursor-not-allowed'
                        : ''
                    "
                    content="Recebi meu cartão"
                    v-tippy
                  >
                    <em class="text-black text-xs fas fa-credit-card" />
                  </button>
                </div>
              </template>
            </inertia-table>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL PARA ATIVAR CARTAO -->
    <Jet-dialog-modal :show="modalCVC" :noBgFooter="true">
      <template #title>
        <span
          class="flex justify-center text-black text-opacity-70 font-bold text-xl py-2 mt-2"
        >
          Confirme os 4 últimos dígitos do seu cartão
        </span>
        <hr class="my-2" />
      </template>
      <template #content>
        <div class="flex flex-col items-center space-y-4 w-full">
          <span>
            Digite os 4 últimos dígitos atrás do seu cartão no campo
            abaixo:</span
          >
          <img class="mx-auto" src="images/4digits.svg" width="300" />
        </div>
        <div>
          <jet-label
            class="mb-3"
            for="google_code"
            value="4 últimos digitos do cartão"
          />
          <jet-input
            @input="checkFourDigit"
            id="last4Digits"
            type="text"
            :placeholder="'Insira o código'"
            class="mt-1 block w-full"
            v-model="last4Digits"
          />
          <div class="mt-2 flex items-center">
            <div
              v-if="last4Digits && !isValidNumber"
              class="bg-red-200 text-red-500 rounded-md py-2 w-full"
            >
              <span class="ml-4">Código inválido</span>
            </div>
            <div
              v-if="last4Digits && isValidNumber"
              class="bg-green-200 text-green-500 rounded-md py-2 w-full"
            >
              <span class="ml-4">Código válido</span>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-between">
          <button-cancel @click.native="modalCVC = false">
            Cancelar
          </button-cancel>
          <modal-password
            :isValidNumber="isValidNumber"
            :card="selectedCard"
            :customer="customer"
            :update="false"
            @close-modal-cvc="modalCVC = false"
          />
        </div>
      </template>
    </Jet-dialog-modal>

    <!-- MODAL DE SOLICITAR CARTÃO  -->
    <Jet-dialog-modal :show="requestCard" :noBgFooter="true">
      <template #title>
        <span class="text-black text-opacity-70 font-bold text-xl py-2 mt-4">
          {{
            !hasVirtuaActive && hasFisicoActive
              ? "Solicitar cartão virtual"
              : "Solicitar novo cartão"
          }}
        </span>
        <hr class="my-2" />
      </template>
      <template #content>
        <div class="flex flex-col space-y-4 w-full">
          <span
            v-if="!hasVirtuaActive && hasFisicoActive"
            class="text-base text-black text-opacity-60"
          >
            Entendemos que possuir um cartão virtual pode ser conveniente para
            suas transações e compras do dia a dia. Para garantir uma
            experiência segura e satisfatória, é necessário que você possua um
            cartão ativo em sua conta antes de solicitar um novo.
          </span>
          <button
            v-if="!hasVirtuaActive && hasFisicoActive"
            class="border border-gray-300 py-2 rounded-md"
            @click="showModalRequest('virtual')"
          >
            <div
              class="flex items-center justify-between w-full text-base font-medium text-black text-opacity-70"
            >
              <span>
                <em class="ml-5 mr-3 fas fa-mobile-alt"></em>
                Cartão virtual
              </span>

              <em class="mr-5 fas fa-angle-right"></em>
            </div>
          </button>
          <button
            v-else
            class="border border-gray-300 py-2 rounded-md"
            @click="showModalRequest('fisico')"
          >
            <div
              class="flex items-center justify-between w-full text-base font-medium text-black text-opacity-70"
            >
              <span>
                <em class="ml-5 mr-3 fas fa-mobile-alt"></em>
                Cartão Físico
              </span>

              <em class="mr-5 fas fa-angle-right"></em>
            </div>
          </button>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-center">
          <button-cancel @click.native="requestCard = false">
            Cancelar
          </button-cancel>
        </div>
      </template>
    </Jet-dialog-modal>

    <!-- MODAL SOLICITAR CARTÃO VIRTUAL -->
    <Jet-dialog-modal :show="requestVirtual" :noBgFooter="true">
      <template #title>
        <span class="text-black text-opacity-70 font-bold text-xl py-2 mt-4">
          <i
            class="mr-2 fas fa-arrow-left cursor-pointer"
            @click="requestVirtual = false"
          />
          Selecione um cartão físico para gerar o seu cartão de crédito virtual.
        </span>
        <hr class="my-2" />
      </template>
      <template #content>
        <div class="flex flex-col space-y-4">
          <div class="text-base text-black text-opacity-60">
            A geração de um cartão de crédito virtual está diretamente ligada a
            um cartão físico já existente em sua conta. Por favor, escolha um
            cartão físico disponível abaixo para prosseguir com a criação do seu
            cartão virtual.
          </div>
          <div class="flex flex-col mt-5">
            <div class="grid grid-cols-6">
              <span
                class="col-span-3 text-center text-xs font-semibold text-black text-opacity-60"
              >
                Número do cartão
              </span>
              <span
                class="col-span-3 text-center text-xs font-semibold text-black text-opacity-60"
              >
                Data de validade
              </span>
            </div>

            <div
              class="grid grid-cols-9 border border-gray-200 w-full py-4 mt-5"
              :class="{ 'border-green-500': isSelectedRadio(item) }"
              v-for="item in cards.list.prepaid_cards"
              :key="item.last_4_digits"
            >
              <input
                class="h-4 w-4 border-gray-300 ml-3 checked:bg-blue-500"
                type="radio"
                name="selectedCard"
                :value="item.uuid"
                v-model="formRequestCard.uuid"
              />
              <span
                class="col-span-4 text-left ml-6 text-sm font-semibold text-black text-opacity-60"
              >
                **** **** **** {{ item.last_4_digits }}
              </span>
              <span
                class="col-span-4 text-center mr-12 text-sm font-semibold text-black text-opacity-60"
              >
                {{ formatExpirationDate(item.expiration_date) }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between">
          <button-cancel
            @click.native="
              requestVirtual = false;
              formRequestCard.reset();
            "
          >
            Cancelar
          </button-cancel>

          <button-confirmation
            class="ml-2"
            :class="{ 'opacity-25': formRequestCard.processing }"
            :disabled="formRequestCard.processing"
            @click.native="showModalRequestVirtual"
          >
            Confirmar
          </button-confirmation>
        </div>
      </template>
    </Jet-dialog-modal>

    <jet-confirmation-modal
      :show="ModalauthorizedVirtual"
      @close="ModalauthorizedVirtual = false"
      :scroll="false"
      :max-width="'xl'"
      :noBgFooter="true"
    >
      <template #title>
        <span class="text-black text-opacity-70 font-bold text-xl py-2 mt-4">
          <i
            class="mr-2 fas fa-arrow-left"
            @click="(requestCard = true), (ModalauthorizedVirtual = false)"
          />
          Confirme a solicitação com o código 2FA
        </span>
        <hr class="my-2" />
      </template>

      <template #content>
        <div class="flex flex-col space-y-4">
          <div class="text-base text-black text-opacity-60">
            Autorize a solicitação através do seu código 2FA.
          </div>
          <div>
            <jet-label class="mb-3" for="google_code" value="Código de 2FA" />
            <jet-input
              id="google_code"
              type="text"
              :placeholder="'Ex: 102330'"
              class="mt-1 block w-full"
              v-model="formRequestCard.google_code"
            />
            <jet-input-error
              :message="formRequestCard.error('google_code')"
              class="mt-2"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between">
          <button-cancel @click.native="ModalauthorizedVirtual = false">
            Cancelar
          </button-cancel>

          <button-confirmation
            class="ml-2"
            :class="{ 'opacity-25': formRequestCard.processing }"
            :disabled="formRequestCard.processing"
            @click.native="requestVirtualCard"
          >
            Confirmar
          </button-confirmation>
        </div>
      </template>
    </jet-confirmation-modal>

    <!-- MODAL DE SOLICITAR REPORT -->
    <jet-confirmation-modal
      :noBgFooter="true"
      :show="reportModal"
      @close="reportModal = false"
      :scroll="false"
      :max-width="'xl'"
    >
      <template #title>
        <span class="text-black text-opacity-70 font-bold text-xl py-2 mt-4">
          Reportar problema
        </span>
        <hr class="my-2" />
      </template>

      <template #content>
        <div class="flex flex-col space-y-4">
          <div class="text-base text-black text-opacity-60">
            Houve algum problema com a entrega do cartão ou com a sua
            solicitação?
          </div>
          <div>
            <jet-label for="google_code" value="Problema" />
            <jet-auto-complete
              class="mt-1"
              :options-list="reasonList"
              v-model="reportForm.reason"
            />
            <jet-input-error
              :message="reportForm.error('reason')"
              class="mt-2"
            />
          </div>
          <div v-if="reportForm.reason.id === 'other'">
            <jet-label for="google_code" value="Informe o problema" />
            <jet-textarea
              :placeholder="'Descreva o problema com mais detalhes'"
              :class-name="'rounded-md border border-solid border-black text-gray-700 py-2 px-3'"
              id="google_code"
              type="textarea"
              class="mt-1 block w-full"
              :required="true"
              v-model="reportForm.description"
            />
            <jet-input-error
              :message="reportForm.error('description')"
              class="mt-2"
            />
          </div>
          <div v-if="errorMessageDescription" class="text-xs text-red-600">
            É necessário descrever seu problema.
          </div>
          <div v-if="errorMessage" class="text-xs text-red-600">
            Selecione um motivo antes de confirmar.
          </div>
        </div>

        <div  class="flex flex-col gap-2 mt-8">
            <div class="text-black text-opacity-60 text-base">
                Canais de atendimento:
            </div>
            <a href="mailto:atendimento@2gofintech.com.br" target="_blank" class="w-72">
                <span class="text-base text-black text-opacity-60 font-bold">
                    <i class="text-yellow-400 far fa-envelope"></i>
                    atendimento@2gofintech.com.br
                </span>
            </a>
                <a href="https://api.whatsapp.com/send/?phone=5511970234560" target="_blank" class="w-44">
                <span class="text-base text-black text-opacity-60 font-bold">
                    <i class="text-green-400 fab fa-whatsapp"></i>
                    (11) 97023-4560
                </span>
            </a>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-between">
          <button-cancel @click.native="reportModal = false">
            Cancelar
          </button-cancel>

          <button-confirmation
            class="ml-2"
            :class="{ 'opacity-25': reportForm.processing }"
            :disabled="reportForm.processing"
            @click.native="showModalReport"
          >
            Confirmar
          </button-confirmation>
        </div>
      </template>
    </jet-confirmation-modal>
    <!-- MODAL DE AUTORIZAR REPORT -->
    <jet-confirmation-modal
      :show="modalauthorizedReport"
      @close="modalauthorizedReport = false"
      :scroll="false"
      :max-width="'xl'"
      :noBgFooter="true"
    >
      <template #title>
        <div class="text-black text-opacity-70 font-bold text-xl py-2">
          <i
            class="mr-2 fas fa-arrow-left"
            @click="modalauthorizedReport = false"
          />
          Confirme a solicitação com o código 2FA
        </div>
        <hr class="my-2" />
      </template>

      <template #content>
        <div class="flex flex-col space-y-4">
          <div class="text-base text-black text-opacity-60 mt-4">
            Autorize a solicitação através do seu código 2FA.
          </div>
          <div>
            <jet-label for="google_code" value="Código de Autorização" />
            <jet-input
              id="google_code"
              type="text"
              class="mt-1 block w-full"
              v-model="reportForm.google_code"
            />
            <jet-input-error
              :message="reportForm.error('google_code')"
              class="mt-2"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between">
          <button-cancel @click.native="modalauthorizedReport = false">
            Cancelar
          </button-cancel>

          <button-confirmation
            class="ml-2"
            :class="{ 'opacity-25': reportForm.processing }"
            :disabled="reportForm.processing"
            @click.native="sendReport"
          >
            Confirmar
          </button-confirmation>
        </div>
      </template>
    </jet-confirmation-modal>
  </app-layout>
</template>
<script>
import AppLayout from "./../../Layouts/AppLayout";
import Welcome from "./../../Jetstream/Welcome";
import JetConfirmationModal from "./../../Jetstream/ConfirmationModal";
import ApplicationMark from "./../../Jetstream/ApplicationMark";
import JetDialogModal from "./../../Jetstream/DialogModal";
import Flash from "../../Helpers/Flash";
import InertiaTable from "../../Helpers/Tables/InertiaTable";
import TransferReceipt from "../Transfer/Receipt";
import BoletoReceipt from "../Boleto/Receipt";
import Balance from "../../Helpers/Balance";
import JetLabel from "../../Jetstream/Label";
import JetInput from "../../Jetstream/Input";
import JetInputError from "../../Jetstream/InputError";
import JetSectionBorder from "../../Jetstream/SectionBorder";
import JetAutoComplete from "../../Jetstream/AutoComplete";
import JetTextarea from "../../Jetstream/Textarea";
import ButtonConfirmation from "../../Jetstream/ButtonConfirmation.vue";
import ButtonCancel from "../../Jetstream/ButtonCancel.vue";
import ModalPassword from "./components/ModalPassword.vue";

export default {
  components: {
    Balance,
    AppLayout,
    Flash,
    InertiaTable,
    TransferReceipt,
    BoletoReceipt,
    Welcome,
    JetConfirmationModal,
    JetDialogModal,
    ApplicationMark,
    JetLabel,
    JetInput,
    JetInputError,
    JetSectionBorder,
    JetAutoComplete,
    JetTextarea,
    ButtonConfirmation,
    ButtonCancel,
    ModalPassword,
  },
  props: ["cards", "customer", "card"],
  data() {
    return {
      typeCard: null,
      requestValidation: false,
      isValidNumber: false,
      last4Digits: "",
      requestCard: false,
      Modalauthorized: false,
      ModalauthorizedVirtual: false,
      modalCVC: false,
      modalauthorizedReport: false,
      requestVirtual: false,
      errorMessage: false,
      errorMessageDescription: false,
      selectedCard: null,
      hasVirtuaActive: null,
      hasFisicoActive: null,

      form: this.$inertia.form({
        google_code: null,
        uuid: null,
      }),
      formRequestCard: this.$inertia.form({
        google_code: null,
        uuid: null,
      }),
      columns: [
        {
          text: "Status",
          className: "text-left",
          key: "status",
          sortable: false,
        },
        {
          text: "Solicitação",
          className: "text-left",
          key: "request",
          sortable: false,
        },
        {
          text: "Solicitante",
          className: "text-left",
          key: "requester",
          sortable: false,
        },
        {
          text: "Data da solicitação",
          className: "text-left",
          key: "request_date",
          sortable: false,
        },
        {
          text: "Previsão de entrega",
          className: "text-left",
          key: "delivery_forecast",
          sortable: false,
        },
        {
          text: "",
          className: "text-left",
          key: "action",
          sortable: false,
        },
      ],
      params: {
        sort: "request_date:desc",
        page: 1,
        ...this.$page.props.queryString,
      },
      reportModal: false,
      reportForm: this.$inertia.form(
        {
          _method: "POST",
          google_code: null,
          uuid: null,
          reason: {
            id: null,
            name: null,
          },
          description: null,
        },
        {
          resetOnSuccess: false,
        }
      ),
      reasonList: [
        {
          id: "not_arrived",
          name: "Meu cartão não chegou",
        },
        {
          id: "too_long_status_change",
          name: "O status da solicitação está demorando para alterar",
        },
        {
          id: "other",
          name: "Outro",
        },
      ],
    };
  },
  created() {
    this.hasVirtuaActive =
      (this.cards.list.virtual_card ?? []).some(
        (card) => card.status === "active" ||  card.status === "locked"
      ) &&
      (this.cards.list.prepaid_cards ?? []).some(
        (card) => card.status !== "active" &&  card.status !== "locked"
      );
    this.hasFisicoActive = (this.cards.list.prepaid_cards ?? []).some((card) =>
      ["active", "created", "processing", "pre_register", "locked"].includes(card.status)
    );
    this.requestValidation = this.hasVirtuaActive && this.hasFisicoActive;
  },
  methods: {
    redirectIndex() {
      this.$inertia.get(this.route("card.index"));
    },
    isSelectedRadio(item) {
      return this.formRequestCard.uuid === item.uuid;
    },
    checkFourDigit() {
      return (this.isValidNumber =
        this.last4Digits === this.form.last_4_digits);
    },
    formatExpirationDate(date) {
      return date ? date.substr(0, 2) + "/" + date.substr(2, 2) : "**/**";
    },
    calcDeliveryForecast(date, state) {
      if (typeof state === "undefined") {
        return "-----";
      }
      const days = state === "SP" ? 10 : 15;
      const forecast = new Date(date);
      forecast.setDate(forecast.getDate() + days);
      return forecast.toLocaleDateString("pt-BR");
    },
    showModalRequest(type) {
      if (type === "virtual") {
        this.typeCard = type;
        this.requestVirtual = true;
      } else {
        this.typeCard = type;
        this.requestCard = false;
        this.ModalauthorizedVirtual = true;
      }
    },
    showModalRequestVirtual() {
      this.requestVirtual = false;
      this.ModalauthorizedVirtual = true;
    },
    requestVirtualCard() {
      if (this.typeCard === "virtual") {
        this.formRequestCard.post(this.route("card.virtual-card-request"), {
          preserveState: false,
          onError: () => {
            this.formRequestCard.processing = false;
          },
          onSuccess: () => {
              this.formRequestCard.processing = false;
            this.formRequestCard.reset();
          },
        });
      } else {
        this.formRequestCard.post(this.route("card.request"), {
          preserveScroll: true,
          onError: () => {
            this.formRequestCard.processing = false;
            this.ModalauthorizedVirtual = false;
            this.requestCard = false;
          },
          onSuccess: () => {
            this.formRequestCard.processing = false;
          },
        });
      }
    },
    showPasswordModal(item) {
      this.modalCVC = true;
      this.selectedCard = item;
      this.form.last_4_digits = item.last_4_digits;
    },
    showReportModal(item) {
      this.reportModal = true;
      this.reportForm.uuid = item.uuid;
    },
    showModalReport() {
      if (this.reportForm.reason && this.reportForm.reason?.id === "other") {
        if (this.reportForm.description === null) {
          return (this.errorMessageDescription = true);
        }
      }
      if (this.reportForm.reason?.id === null) {
        this.errorMessage = true;
      } else {
        this.errorMessage = false;
        this.modalauthorizedReport = true;
        this.reportModal = false;
      }
    },
    sendReport() {
      this.reportForm.post(this.route("card.send-report"), {
        preserveScroll: true,
        onError: () => {
          this.reportForm.processing = false;
        },
        onSuccess: () => {
          this.reportModal = false;
        },
      });
    },
    getStatus(request_status) {
      switch (request_status) {
        case "complete":
          return {
            class: "bg-green-400 text-white",
            icon: "ml-2 fas fa-check",
            name: "Entregue",
          };
        case "rejected":
          return {
            class: "bg-red-400 text-white",
            icon: "fas fa-times",
            name: "Solicitação recusada",
          };
        case "waiting":
          return {
            class: "bg-yellow-500 text-white",
            icon: "fas fa-hourglass-end",
            name: "Aguardando análise",
          };
        case "processing":
          return {
            class: "bg-gray-400 text-white",
            icon: "fas fa-sync",
            name: "Em processamento",
          };
        case "approved":
          return {
            class: "bg-blue-400 text-white",
            icon: "fas fa-truck",
            name: "Aprovado e aguardando entrega",
          };
        default:
          return {
            class: "text-black font-bold",
            name: "Solicitação não encontrada",
          };
      }
    },
  },
};
</script>
